import { ChessRules, normalizeUci, type Color } from '../chess/ChessRules';
export interface PuzzleDefinition {
  id: string;
  fenBefore: string;
  setupMove: string;
  playerFen: string;
  solutionMoves: string[];
  solverColor: Color;
  rating: number;
  ratingDeviation: number;
  popularity: number;
  playCount: number;
  themes: string[];
  sourceGameUrl: string;
  openingTags: string[];
  dailyDate?: number;
  datasetVersion: string;
}
export function definePuzzle(
  input: Omit<PuzzleDefinition, 'playerFen' | 'solverColor'>,
): PuzzleDefinition {
  if (!/^[a-zA-Z0-9_-]{1,64}$/.test(input.id)) throw new Error('Invalid puzzle ID');
  if (!input.solutionMoves.length || input.solutionMoves.length % 2 !== 1)
    throw new Error('Solution must end on a player move');
  const chess = new ChessRules(input.fenBefore);
  const setupMove = normalizeUci(input.setupMove);
  chess.play(setupMove);
  const playerFen = chess.fen,
    solverColor = chess.turn;
  const solutionMoves = input.solutionMoves.map(normalizeUci);
  for (const move of solutionMoves) chess.play(move);
  return { ...input, setupMove, solutionMoves, playerFen, solverColor };
}
export function validatePuzzle(input: unknown): PuzzleDefinition {
  if (!input || typeof input !== 'object') throw new Error('Invalid puzzle record');
  const p = input as PuzzleDefinition;
  for (const key of [
    'id',
    'fenBefore',
    'playerFen',
    'setupMove',
    'sourceGameUrl',
    'datasetVersion',
  ] as const)
    if (typeof p[key] !== 'string' || p[key].length > 512) throw new Error(`Invalid ${key}`);
  for (const key of ['solutionMoves', 'themes', 'openingTags'] as const)
    if (
      !Array.isArray(p[key]) ||
      p[key].length > 128 ||
      p[key].some((value) => typeof value !== 'string' || value.length > 256)
    )
      throw new Error(`Invalid ${key}`);
  for (const [key, min, max] of [
    ['rating', 0, 4000],
    ['ratingDeviation', 0, 1000],
    ['popularity', -100, 100],
    ['playCount', 0, 2147483647],
  ] as const)
    if (!Number.isSafeInteger(p[key]) || p[key] < min || p[key] > max)
      throw new Error(`Invalid ${key}`);
  if (
    p.sourceGameUrl &&
    !/^https:\/\/lichess\.org\/[a-zA-Z0-9]{8}(?:\/(?:white|black))?(?:#\d+)?$/.test(p.sourceGameUrl)
  )
    throw new Error('Invalid source game URL');
  const valid = definePuzzle(p);
  if (valid.playerFen !== p.playerFen || valid.solverColor !== p.solverColor)
    throw new Error('Inconsistent setup position');
  return valid;
}
